"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { G_LOGIN_URL } from "../config/config";

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = React.useState({ email: "", password: "" });
  const [errors, setErrors] = React.useState({ email: "", password: "" });
  const [showModal, setShowModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loginError, setLoginError] = React.useState(""); // Backend login errors
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false); // NEW

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Frontend validation
  const validateForm = () => {
    const newErrors = { email: "", password: "" };

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Enter a valid email";

    if (!formData.password.trim()) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const response = await login(formData.email, formData.password);
    setIsSubmitting(false);

    if (response.success) {
      setShowModal(true);
      setFormData({ email: "", password: "" });
      setErrors({ email: "", password: "" });
      setLoginError(""); // Clear any previous backend error
    } 
    else if (
    response.success === false &&
    response.message === "User account is not active"
  ) {
    setLoginError(response.message);
    navigate("/pendingverify");
  }
  else {
      setLoginError(response.message || "Login failed");
    }
  };

  // Google Sign-In (placeholder)
  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setLoginError("");

    try {
      console.log("Google Sign In clicked");
      alert("Google Sign In integration coming soon!");
    } catch (error) {
      setLoginError("Google Sign In failed. Please try again.");
      console.error("Google Sign In error:", error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleReturnToStore = () => navigate("/");
  const handleSignUp = () => navigate("/signup");
  const handleForgotPassword = () => navigate("/forgotpassword");
  const closeModal = () => {
    setShowModal(false);
    navigate("/");
  };

  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: { scaleX: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { scaleX: 0, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  const AnimatedLink = ({ children, onClick, ariaLabel, className = "" }) => (
    <motion.button
      onClick={onClick}
      className={`relative text-base cursor-pointer text-yellow-500 bg-transparent border-none p-0 hover:opacity-80 outline-none ${className}`}
      aria-label={ariaLabel}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );

  const GoogleIcon = () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="flex flex-col items-center gap-10 p-10 bg-white rounded-3xl shadow-lg max-w-md w-full">
          {/* Header */}
          <header className="text-center">
            <h1 className="text-4xl font-bold text-yellow-500">Welcome Back</h1>
            <p className="mt-2 text-gray-600 text-sm">Sign in to continue</p>

            {loginError && (
              <p className="mt-4 text-red-500 font-medium text-sm">{loginError}</p>
            )}
          </header>

          {/* Sign Up Link */}
          <div className="flex justify-center gap-2 text-gray-700 text-sm">
            <span>Don't have an account?</span>
            <AnimatedLink onClick={handleSignUp} ariaLabel="Sign up for a new account">
              Sign up here
            </AnimatedLink>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full" noValidate>
            <div className="flex flex-col">
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Email"
                className={`px-4 py-3 rounded-lg border transition-colors outline-none ${
                  errors.email ? "border-red-500" : "border-yellow-300"
                } focus:ring-2 focus:ring-yellow-300`}
              />
            </div>

            <div className="flex flex-col">
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Password"
                className={`px-4 py-3 rounded-lg border transition-colors outline-none ${
                  errors.password ? "border-red-500" : "border-yellow-300"
                } focus:ring-2 focus:ring-yellow-300`}
              />
            </div>

            <div className="flex justify-between items-center pt-4">
              <button
                type="submit"
                disabled={isSubmitting || isGoogleLoading}
                className="px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Signing in..." : "Sign In"}
              </button>
              <AnimatedLink
                onClick={handleForgotPassword}
                ariaLabel="Reset your password"
                className="text-sm"
              >
                Forgot your password?
              </AnimatedLink>
            </div>
          </form>

          {/* Divider */}
          <div className="flex items-center w-full">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm font-medium">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Google Sign In Button */}
          <div className="w-full">
          <button
            type="button"
            onClick={() => window.location.href = `${G_LOGIN_URL}`}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
            aria-label="Sign in with Google"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </button>

          </div>

          {/* Return to Store */}
          <div className="flex justify-center pt-4">
            <AnimatedLink
              onClick={handleReturnToStore}
              ariaLabel="Return to store homepage"
              className="text-sm"
            >
              Return to Store
            </AnimatedLink>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-yellow-100 mb-4">
                <svg
                  className="h-7 w-7 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-yellow-500 mb-2">
                Successfully Logged In!
              </h2>
              <p className="text-gray-600 mb-6">
                Welcome back! You have been successfully logged into your account.
              </p>
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg shadow transition-all"
              >
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default Login;
